import { InternalTransferSubmit, ISubmit, RegularISubmit } from '@interfaces';
import {
  isMultiPostPayload,
  MultiSignerPostPayload,
  MultiSignerPostPayloadMulti,
  MultiSignerTransferToWithPositions,
  MXMPosition,
  MXMTaxLot,
} from '@interfaces/models/multi-signer-approval';
import { AvgCostConversion } from '@interfaces/models/spec-id-fallback.enum';
import { MultiITToAccount } from '@interfaces/state';
import { createSelector } from '@ngrx/store';
import {
  AccountTypes,
  FlowTypes,
  ISelectedAccount,
  IUserInputAccount,
  YesNoEmpty,
} from '@shared/models';
import {
  selectAllMultiITToAccounts,
  selectMultiITFromAvgCostConversion,
  selectMultiITFromSelectedAccount,
  selectMultiITFromTransferMethod,
  selectMultiITTransferType,
  selectMultiITTriageTransferFrom,
} from '@store/internal-transfers/selectors/multi-internal-transfer.selectors';
import { selectMultiITCO0Payloads } from '@store/internal-transfers/selectors/submit/multi-it-submit.selectors';
import { CostBasisPosition } from '@store/reducers/cost-basis/cost-basis.reducer';
import { GiftingState } from '@store/reducers/gifting/gifting.reducer';
import { InternalTransferState } from '@store/reducers/internal-transfer/internal-transfer.reducer';
import * as fromMXMState from '@store/reducers/mxm/mxm.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { selectItMultiReceivingToggle } from '@store/selectors/feature/feature.selectors';
import { getTaxLotsAmount, getTotalAmountForPosition } from 'sharedUtils/utils.helpers';

import { getMXMState } from '../../reducers/index';
import { selectSelectedPositions } from '../cost-basis/cost-basis.selectors';
import { selectGiftingState } from '../gifting/gifting.selectors';
import { selectInternalTransferState } from '../internal-transfer/internal-transfer.selectors';
import { selectSubmitMetricsID } from '../open-account/open-account.selectors';
import { assertsIsCurrentAccount } from '../receipt/internal-transfer/cashier-internal-transfer';
import { selectedAccountsFrom } from '../selected-client-accounts/selected-client-accounts.selectors';
import { getITSubmitPayload, getSubmitPayload } from '../submit/submit.selectors';
import { selectTriageState } from '../triage/triage.selectors';

export const selectMXMState = createSelector(getMXMState, (state) => state);
export const selectMXMApprovalStatus = createSelector(
  selectMXMState,
  fromMXMState.getMXMApprovalStatus,
);
export const selectMXMTransferId = createSelector(selectMXMState, fromMXMState.getMXMTransferId);
export const selectMXMSignState = createSelector(selectMXMState, fromMXMState.getMXMSignState);
export const selectMXMTransferStatus = createSelector(
  selectMXMState,
  fromMXMState.getMXMTransferStatus,
);

export const selectIsFinalSigner = createSelector(selectMXMState, fromMXMState.getIsFinalSigner);

export const getMultiITMXMPostPayload = createSelector(
  selectMultiITTriageTransferFrom,
  selectMultiITFromSelectedAccount,
  selectMultiITTransferType,
  selectMultiITFromTransferMethod,
  selectMultiITFromAvgCostConversion,
  selectAllMultiITToAccounts,
  selectMultiITCO0Payloads,
  selectSubmitMetricsID,
  (
    triageFrom,
    fromAccount,
    transferType,
    transferMethod,
    avgCostConversion,
    toAccounts,
    multiITCO0Payloads,
    metricsCorrelationID,
  ): MultiSignerPostPayloadMulti => {
    const fromAccountData = fromAccount?.accountData;
    return {
      triage: {
        transferFrom: triageFrom,
        transferTo: AccountTypes.EMPTY,
        isOwner: true,
        exceptionsApply: true,
        flowType: FlowTypes.INTERNAL_TRANSFER,
      },
      transferFrom: {
        accountName: fromAccountData?.accountName ?? '',
        accountNumber: fromAccountData?.accountNumber ?? '',
        accountType: fromAccountData?.accountType ?? '',
        accountId: fromAccountData?.accountId ?? '',
        transferType,
        transferMethod,
        convertAvgCostHoldings: avgCostConversion === AvgCostConversion.FIFO,
        convertSpecIDHoldings: false,
      },
      transferToAccounts: toAccounts.map(getTransferToWithPositions),
      metricsCorrelationID,
      filenet: multiITCO0Payloads,
    };
  },
);

export const getMultiSignerPostPayload = createSelector(
  selectTriageState,
  selectGiftingState,
  selectInternalTransferState,
  selectedAccountsFrom,
  selectSelectedPositions,
  getITSubmitPayload,
  getSubmitPayload,
  selectSubmitMetricsID,
  selectItMultiReceivingToggle,
  getMultiITMXMPostPayload,
  (
    triageState,
    giftingState,
    ITState,
    accountsFrom,
    selectedPositions,
    itSubmitPayload,
    submitPayload,
    metricsCorrelationID,
    isMultiReceivingToggleOn,
    multiITPayload,
  ): MultiSignerPostPayload => {
    // selectedAccountsFrom is `ISelectedAccount[] | null`; guard before destructuring
    // so strict null checks are satisfied (behaviour is unchanged — a null/empty list
    // leaves `account` undefined, which the try/catch below already tolerates).
    const [account] = accountsFrom ?? [];
    try {
      const { flowType } = triageState;
      switch (flowType) {
        case FlowTypes.INTERNAL_TRANSFER:
          return isMultiReceivingToggleOn
            ? multiITPayload
            : getInternalTransferPayload(
                ITState,
                selectedPositions,
                itSubmitPayload,
                metricsCorrelationID,
              );

        case FlowTypes.GIFTING:
          return getGiftingPayload(
            giftingState,
            triageState,
            account,
            selectedPositions,
            submitPayload,
            metricsCorrelationID,
          );

        default:
          throw new Error('Invalid Flow Type');
      }
    } catch (e) {
      //for whatever reason, catching this works. I think it's because this selector is constructed of
      //many selectors that point at slices of state that constantly get updated, such that this thing errors out bc it's missing data whenever those slices get updated
    }
  },
);

/**
 * True when the fully-signed MxM transfer targets multiple receiving accounts
 * (the payload carries `transferToAccounts`). Used by the final-signer effect to
 * route the submit to the Multi-IT CO0 path instead of the single-IT path.
 * `getMultiSignerPostPayload` can resolve to `undefined` (its selectors briefly
 * lack data mid-update), so guard before narrowing.
 */
export const selectIsMultiITMxm = createSelector(getMultiSignerPostPayload, (payload) =>
  payload ? isMultiPostPayload(payload) : false,
);

function getTransferToWithPositions(account: MultiITToAccount): MultiSignerTransferToWithPositions {
  const selected = account.selectedAccount;
  const accountName = assertsIsCurrentAccount(account.selectedAccount)
    ? account.selectedAccount.accountData.accountName
    : account.selectedAccount.accountName;
  return {
    accountNumber: selected.accountNumber,
    accountName,
    positions: getPositionPayload(account.positions, FlowTypes.INTERNAL_TRANSFER),
  };
}

function getInternalTransferPayload(
  state: InternalTransferState,
  selectedPositions: CostBasisPosition[],
  itPayload: InternalTransferSubmit,
  metricsCorrelationID: string,
): MultiSignerPostPayload {
  const from = state.transferFrom;
  const fromAccount = from.selectedAccount.accountData;
  const to = state.transferTo.selectedAccount;
  const triage = state.triage;
  return {
    triage: {
      transferFrom: triage.transferFrom,
      transferTo: triage.transferTo,
      isOwner: triage.isOwner,
      exceptionsApply: triage.exceptionsApply,
      flowType: FlowTypes.INTERNAL_TRANSFER,
    },
    transferFrom: {
      accountName: fromAccount.accountName,
      accountNumber: fromAccount.accountNumber,
      accountType: fromAccount.accountType,
      accountId: fromAccount.accountId,
      transactionType: state.transactionType,
      transferType: from.transferType,
      transferMethod: from.transferMethod,
      percentageOfHoldings: +from.percentageOfHoldings,
      convertAvgCostHoldings: from.convertAvgCostHoldings,
      convertSpecIDHoldings: from.convertSpecIDHoldings,
    },
    transferTo: {
      accountNumber: to.accountNumber,
      accountName:
        (to as IUserInputAccount).accountName || (to as ISelectedAccount).accountData.accountName,
    },
    positions: getPositionPayload(selectedPositions, FlowTypes.INTERNAL_TRANSFER),
    metricsCorrelationID,
    filenet: itPayload,
  };
}

function getGiftingPayload(
  giftingState: GiftingState,
  triageState: TriageState,
  account: ISelectedAccount,
  selectedPositions: CostBasisPosition[],
  submitPayload: RegularISubmit,
  metricsCorrelationID: string,
): MultiSignerPostPayload {
  const { recipient } = giftingState;
  return {
    triage: {
      transferFrom: triageState.accountTypeInheriting,
      transferTo: AccountTypes.EMPTY,
      flowType: FlowTypes.GIFTING,
      giftingDesignation: triageState.giftingDesignation,
    },
    transferFrom: {
      accountNumber: account.accountNumber,
      accountType: triageState.accountTypeInheriting,
      accountName: account.accountData.accountName.split(' — ')[0],
      accountId: account.accountData.accountId,
    },
    transferTo: {
      accountNumber: recipient.accountNumber,
      accountName: recipient.name,
      giftRecipientType: recipient.type,
      anonymousGift: recipient.anonymousGift === YesNoEmpty.YES ? YesNoEmpty.YES : YesNoEmpty.NO,
      dtcNumber: recipient.dtcNumber,
      firm: recipient.firm,
    },
    positions: getPositionPayload(selectedPositions, FlowTypes.GIFTING),
    metricsCorrelationID,
    filenet: submitPayload,
  };
}

const getPositionPayload = (
  selectedPositions: CostBasisPosition[],
  flowType: FlowTypes,
): MXMPosition[] => {
  return selectedPositions.map((position) => {
    const payloadPosition: MXMPosition = {
      positionId: position.positionId,
      ticker: position.ticker,
      selectedAmount: `${getTotalAmountForPosition(position, flowType)}`,
      selectedCostBasisMethod: position.selectedCostBasisMethod,
      sharesToTransfer:
        flowType === FlowTypes.INTERNAL_TRANSFER
          ? String(position.sharesToTransfer)
          : String(position.shareQuantity),
    };
    if (position.shownCostBasisMethod !== null) {
      payloadPosition['shownCostBasisMethod'] = position.shownCostBasisMethod;
    }
    if (position.percentOfHolding !== null) {
      payloadPosition['percentOfHolding'] = String(position.percentOfHolding);
    }

    if (flowType === FlowTypes.GIFTING) {
      payloadPosition.giftingMethod = position.giftingMethod;

      if (position.taxLots) {
        payloadPosition.taxLots = position.taxLots
          .filter((taxLot) => {
            const amount = getTaxLotsAmount(position.giftingMethod, taxLot);
            return amount > 0;
          })
          .map((taxLot) => {
            const giftingTaxLot: MXMTaxLot = {
              lotID: taxLot.lotID,
              quantity: getTaxLotsAmount(position.giftingMethod, taxLot),
            };
            return giftingTaxLot;
          });
      }
      if (position.additionalTaxLot) {
        payloadPosition.additionalTaxLotId = position.additionalTaxLot.lotID;
      }
    }
    return payloadPosition;
  });
};
